import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../api";

const STATUS_COLOR  = { not_sent: "#888", sent_to_jury: "#f59e0b", scored: "#10b981", winner: "#6366f1", not_selected: "#ef4444", honorary: "#0891b2" };
const STATUS_LABELS = { sent_to_jury: "Sent to Jury", scored: "Scored", winner: "🏆 Winner", honorary: "Honorary", not_selected: "Not Selected" };
const FINAL_STATUSES = ["winner", "not_selected", "honorary"];

function getNomineeName(data) {
  if (!data) return "—";
  const first = data.firstName || data.firstname || data.FirstName || "";
  const last  = data.lastName  || data.lastname  || data.LastName  || "";
  if (first || last) return [first, last].filter(Boolean).join(" ");
  const nameKeys = ["fullname", "fullName", "name", "candidatename", "nomineename", "applicantname"];
  const entry = Object.entries(data).find(([k]) =>
    nameKeys.some(nk => k.toLowerCase().replace(/\s+/g, "") === nk)
  );
  return entry ? String(entry[1]) : "—";
}

const CAT_PALETTE = ["#1D9AEC","#6366f1","#10b981","#f59e0b","#0891b2","#ec4899","#8b5cf6","#ef4444"];

export default function JuryNominationsPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [nominations, setNominations] = useState(null);
  const [myScores,    setMyScores]    = useState({}); // { submissionId: score }
  const [tab,         setTab]         = useState("individual");
  const [viewMode,    setViewMode]    = useState("list");
  const [catFilter,   setCatFilter]   = useState("");
  const [search,      setSearch]      = useState("");
  const [scoreFilter, setScoreFilter] = useState("all"); // all | pending | scored | recused

  useEffect(() => {
    Promise.all([
      api.get(`/jury/my-nominations/${eventId}`),
      api.get(`/jury/scores/my-event/${eventId}`),
    ]).then(([nomRes, scoreRes]) => {
      setNominations(nomRes.data.data || []);
      const map = {};
      (scoreRes.data.data || []).forEach(s => { map[s.submissionId] = s.score; });
      setMyScores(map);
    }).catch(() => setNominations([]));
  }, [eventId]);

  const JURY_VISIBLE = ["sent_to_jury", "scored", "winner", "honorary", "not_selected"];

  const filtered = (nominations || []).filter(n =>
    n.eventFormId?.nominationType === tab && JURY_VISIBLE.includes(n.juryStatus)
  );

  const scoredByMe = (n) => myScores[n._id] != null;

  const displayed = (() => {
    let list = catFilter
      ? filtered.filter(n => (n.nominationCategory || "Uncategorized") === catFilter)
      : filtered;

    if (scoreFilter === "pending") list = list.filter(n => !scoredByMe(n) && !n.recused);
    if (scoreFilter === "scored")  list = list.filter(n => scoredByMe(n));
    if (scoreFilter === "recused") list = list.filter(n => n.recused);

    const q = search.trim().toLowerCase();
    if (q) list = list.filter(n =>
      getNomineeName(n.submissionData).toLowerCase().includes(q) ||
      (n.nominationCategory || "").toLowerCase().includes(q) ||
      (n.nominationSubCategory || "").toLowerCase().includes(q)
    );
    return list;
  })();

  // My own progress — never the panel average, which is other people’s work
  const myPending = filtered.filter(n => !scoredByMe(n) && !n.recused).length;

  const tabCount = (t) => (nominations || []).filter(n =>
    n.eventFormId?.nominationType === t && JURY_VISIBLE.includes(n.juryStatus)
  ).length;

  // Category breakdown
  const categoriesData = (() => {
    const map = {};
    filtered.forEach(n => {
      const cat = n.nominationCategory || "Uncategorized";
      if (!map[cat]) map[cat] = { name: cat, total: 0, scored: 0, pending: 0, recused: 0, subs: {} };
      map[cat].total++;
      if (n.nominationSubCategory) map[cat].subs[n.nominationSubCategory] = (map[cat].subs[n.nominationSubCategory] || 0) + 1;
      // Progress is this juror’s own — myScores, not the panel average
      if (n.recused) map[cat].recused++;
      else if (myScores[n._id] != null) map[cat].scored++;
      else map[cat].pending++;
    });
    return Object.values(map)
      .map(c => ({ ...c, subList: Object.entries(c.subs).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count) }))
      .sort((a, b) => b.total - a.total);
  })();

  const handleTabSwitch = (t) => { setTab(t); setCatFilter(""); setScoreFilter("all"); setSearch(""); };

  return (
    <Layout title="Nominations">
      {/* Back to events */}
      <button onClick={() => navigate("/jury/events")} style={{ marginBottom: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#475569", display: "inline-flex", alignItems: "center", gap: 6, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        ← Back to Events
      </button>

      {/* Tabs + view toggle */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        {["individual", "organisation"].map(t => (
          <button key={t} onClick={() => handleTabSwitch(t)} style={{ padding: "7px 20px", borderRadius: 20, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13, background: tab === t ? "#1D9AEC" : "#e8e8e8", color: tab === t ? "#fff" : "#555" }}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {nominations && (
              <span style={{ marginLeft: 6, background: tab === t ? "rgba(255,255,255,0.25)" : "#d1d5db", color: tab === t ? "#fff" : "#6b7280", borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>
                {tabCount(t)}
              </span>
            )}
          </button>
        ))}

        {/* View toggle */}
        <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 20, padding: 3, gap: 2, marginLeft: 4 }}>
          {[{ mode: "list", icon: "☰", label: "List" }, { mode: "categories", icon: "⊞", label: "By Category" }].map(({ mode, icon, label }) => (
            <button key={mode} onClick={() => setViewMode(mode)} style={{
              padding: "5px 14px", borderRadius: 18, border: "none", cursor: "pointer",
              fontWeight: 600, fontSize: 12,
              background: viewMode === mode ? "#fff" : "transparent",
              color: viewMode === mode ? "#1D9AEC" : "#94a3b8",
              boxShadow: viewMode === mode ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              transition: "all 0.15s",
            }}>{icon} {label}</button>
          ))}
        </div>
      </div>

      {/* Category breadcrumb */}
      {catFilter && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, padding: "10px 16px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10 }}>
          <span style={{ fontSize: 13, color: "#64748b" }}>
            <span style={{ color: "#94a3b8", cursor: "pointer", fontWeight: 600 }} onClick={() => { setCatFilter(""); setViewMode("categories"); }}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </span>{" › "}<strong style={{ color: "#1D9AEC" }}>{catFilter}</strong>
          </span>
          <span style={{ background: "#1D9AEC", color: "#fff", borderRadius: 10, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>{displayed.length}</span>
          <button onClick={() => { setCatFilter(""); setViewMode("categories"); }}
            style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 13, fontWeight: 600 }}>
            ← All Categories
          </button>
        </div>
      )}

      {/* ── Category grid view ── */}
      {viewMode === "categories" && !catFilter && (
        !nominations ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px,1fr))", gap: 14 }}>
            {[1,2,3].map(i => <div key={i} style={{ background: "#fff", borderRadius: 12, padding: 22 }}><div className="skeleton" style={{ height: 14, width: "60%", marginBottom: 8 }} /><div className="skeleton" style={{ height: 10, width: "30%" }} /></div>)}
          </div>
        ) : categoriesData.length === 0 ? (
          <div style={{ background: "#fff", borderRadius: 10, padding: "48px 24px", textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📂</div>
            <p style={{ fontWeight: 600, color: "#374151", margin: "0 0 4px" }}>No categories yet</p>
            <p style={{ color: "#9ca3af", fontSize: 13, margin: 0 }}>No nominations have been assigned to you yet.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px,1fr))", gap: 16 }}>
            {categoriesData.map((cat, idx) => {
              const color   = CAT_PALETTE[idx % CAT_PALETTE.length];
              // Recused nominations are not mine to score, so they leave the denominator
              const scorable = Math.max(cat.total - cat.recused, 0);
              const scorePct = scorable > 0 ? Math.round((cat.scored / scorable) * 100) : 100;
              const pendingCount = cat.pending;
              return (
                <div key={cat.name}
                  onClick={() => { setCatFilter(cat.name); setViewMode("list"); }}
                  style={{ background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: `1px solid ${color}22`, cursor: "pointer", position: "relative", overflow: "hidden", transition: "transform 0.15s, box-shadow 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 20px ${color}25`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"; }}
                >
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: color }} />

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🏷</div>
                    <span style={{ fontSize: 26, fontWeight: 800, color }}>{cat.total}</span>
                  </div>

                  <h3 style={{ margin: "0 0 10px", fontSize: 13.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>{cat.name}</h3>

                  {/* Scored / Pending chips */}
                  <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                    {cat.scored  > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: "#ecfdf5", color: "#059669", padding: "2px 7px", borderRadius: 8 }}>✓ Scored {cat.scored}</span>}
                    {pendingCount > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: "#fffbeb", color: "#d97706", padding: "2px 7px", borderRadius: 8 }}>⏳ Pending {pendingCount}</span>}
                    {cat.recused > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: "#f1f5f9", color: "#64748b", padding: "2px 7px", borderRadius: 8 }}>Recused {cat.recused}</span>}
                  </div>

                  {/* Sub-category split */}
                  {cat.subList.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>
                      {cat.subList.slice(0, 3).map(s => (
                        <span key={s.name} title={`${s.name} — ${s.count}`}
                          style={{ fontSize: 10, fontWeight: 700, background: "#f5f3ff", color: "#6d28d9", padding: "2px 7px", borderRadius: 8, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {s.name} · {s.count}
                        </span>
                      ))}
                      {cat.subList.length > 3 && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8" }}>+{cat.subList.length - 3}</span>
                      )}
                    </div>
                  )}

                  {/* Your scoring progress */}
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#94a3b8", marginBottom: 4 }}>
                      <span>Your progress</span><span>{scorePct}%</span>
                    </div>
                    <div style={{ background: "#f1f5f9", borderRadius: 4, height: 5, overflow: "hidden" }}>
                      <div style={{ width: `${scorePct}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.4s" }} />
                    </div>
                  </div>

                  <div style={{ marginTop: 12, fontSize: 12, fontWeight: 600, color, display: "flex", alignItems: "center", gap: 4 }}>
                    {pendingCount > 0
                      ? `Score ${pendingCount} pending →`
                      : `Review ${scorable} scored →`}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Toolbar — find a nominee, or jump to what is still pending */}
      {(viewMode === "list" || catFilter) && nominations && filtered.length > 0 && (
        <div className="voh-toolbar" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
          <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
            <span aria-hidden="true" style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#94A3B8" }}>🔍</span>
            <input
              type="text"
              aria-label="Search nominations"
              placeholder="Search nominee or category…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: "100%", padding: "9px 14px 9px 32px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, outline: "none", background: "#fff", boxSizing: "border-box", fontFamily: "inherit", color: "#0F172A" }}
            />
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              { key: "all",     label: "All",     count: filtered.length },
              { key: "pending", label: "Pending", count: myPending },
              { key: "scored",  label: "Scored",  count: filtered.filter(scoredByMe).length },
              { key: "recused", label: "Recused", count: filtered.filter(n => n.recused).length },
            ].filter(f => f.key !== "recused" || f.count > 0).map(f => {
              const active = scoreFilter === f.key;
              return (
                <button key={f.key} onClick={() => setScoreFilter(f.key)} aria-pressed={active}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "inherit",
                    background: active ? "#1D9AEC" : "#fff", color: active ? "#fff" : "#475569",
                    border: `1.5px solid ${active ? "#1D9AEC" : "#E2E8F0"}`,
                    padding: "7px 13px", borderRadius: 8, fontSize: 12.5, fontWeight: 600,
                  }}>
                  {f.label}
                  <span style={{ fontSize: 10.5, fontWeight: 700, background: active ? "rgba(255,255,255,0.25)" : "#F1F5F9", color: active ? "#fff" : "#64748B", padding: "1px 7px", borderRadius: 10 }}>
                    {f.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── List view ── */}
      {(viewMode === "list" || catFilter) && (
        !nominations ? (
          <div style={{ background: "#fff", borderRadius: 10, padding: 32, textAlign: "center" }}>
            <div className="skeleton" style={{ height: 14, width: "40%", margin: "0 auto 10px" }} />
            <div className="skeleton" style={{ height: 10, width: "60%", margin: "0 auto" }} />
          </div>
        ) : displayed.length === 0 ? (
          <div style={{ background: "#fff", borderRadius: 10, padding: "48px 24px", textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📋</div>
            <p style={{ fontWeight: 600, color: "#374151", margin: "0 0 4px" }}>No nominations assigned yet</p>
            <p style={{ color: "#9ca3af", fontSize: 13, margin: 0 }}>Ask your administrator to send nominations to the jury.</p>
          </div>
        ) : (
          <div className="voh-table-wrap" style={{ overflowX: "auto", background: "#fff", borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
            <thead>
              <tr style={{ background: "#f8f9fa" }}>
                {["Nominee", "Category", "My Score", "Action"].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map(n => (
                <tr key={n._id} style={{ borderTop: "1px solid #f0f0f0" }}>
                  <td style={td}>{getNomineeName(n.submissionData)}</td>
                  <td style={td}>
                    {n.nominationCategory
                      ? <span onClick={() => { setCatFilter(n.nominationCategory); setViewMode("list"); }}
                          style={{ cursor: "pointer", background: "#f0f9ff", color: "#0369a1", border: "1px solid #bae6fd", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                          {n.nominationCategory}
                        </span>
                      : <span style={{ color: "#ccc" }}>—</span>
                    }
                    {n.nominationSubCategory && (
                      <span style={{ marginLeft: 5, background: "#f5f3ff", color: "#6d28d9", border: "1px solid #ddd6fe", padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                        {n.nominationSubCategory}
                      </span>
                    )}
                  </td>
                  <td style={td}>
                    {n.recused ? (
                      <span title={n.recusalReason || "Conflict of interest"}
                        style={{ fontSize: 12, color: "#64748B", background: "#F1F5F9", padding: "3px 9px", borderRadius: 8, fontWeight: 600 }}>
                        Recused
                      </span>
                    ) : myScores[n._id] != null ? (
                      <span style={{ fontWeight: 700, fontSize: 13, color: "#10B981", background: "#ECFDF5", padding: "3px 9px", borderRadius: 8 }}>
                        {myScores[n._id]}/10
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, color: "#F59E0B", background: "#FFFBEB", padding: "3px 9px", borderRadius: 8, fontWeight: 600 }}>
                        Pending
                      </span>
                    )}
                  </td>
                  <td style={td}>
                    <button onClick={() => navigate(`/jury/score/${n._id}`)} style={{ padding: "5px 14px", background: n.recused ? "#94A3B8" : "#1D9AEC", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>
                      {n.recused ? "View" : myScores[n._id] != null ? "View / Edit" : "Score"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )
      )}
    </Layout>
  );
}

const th = { padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.04em" };
const td = { padding: "12px 16px", fontSize: 14, color: "#333" };
